import React, { useState, useEffect, useRef } from 'react';
import { Box, CardMedia, Fade } from '@mui/material';
import { carouselItems, carouselSettings } from '../config/carouselConfig';
import { InAppBrowser } from '@capacitor/inappbrowser';
import { Capacitor } from '@capacitor/core';

// Function to open URLs in external browser
const openExternalUrl = async (url) => {
    if (Capacitor.isNativePlatform()) {
        try {
            await InAppBrowser.openInExternalBrowser({ url });
        } catch (error) {
            console.error('Error opening external URL:', error);
            window.open(url, '_blank');
        }
    } else {
        window.open(url, '_blank');
    }
};

const ImageCarousel = () => {
    // Inicializar con un índice aleatorio
    const [currentIndex, setCurrentIndex] = useState(() => 
        Math.floor(Math.random() * carouselItems.length)
    );
    const [fadeIn, setFadeIn] = useState(true);
    const videoRef = useRef(null);
    const intervalRef = useRef(null);

    const currentItem = carouselItems[currentIndex];
    const isVideo = currentItem?.type === 'video';
    
    // Obtener duración del item actual (usa duration personalizada si existe, sino usa interval por defecto)
    const getCurrentDuration = () => {
        if (currentItem?.duration) {
            return currentItem.duration;
        }
        return carouselSettings.interval;
    };

    useEffect(() => {
        if (carouselItems.length > 1) {
            const currentDuration = getCurrentDuration();
            
            intervalRef.current = setTimeout(() => {
                // Fade out
                setFadeIn(false);
                
                // After fade out, change content and fade in
                setTimeout(() => {
                    setCurrentIndex((prevIndex) => (prevIndex + 1) % carouselItems.length);
                    setFadeIn(true);
                }, carouselSettings.transitionDuration);
            }, currentDuration);

            return () => {
                if (intervalRef.current) {
                    clearTimeout(intervalRef.current);
                }
            };
        }
    }, [currentIndex]); // Re-ejecutar cuando cambie el index para usar la duración correcta

    // Auto-reproducir video cuando se carga
    useEffect(() => {
        if (isVideo && videoRef.current && fadeIn) {
            videoRef.current.play().catch(err => {
                console.error('Error playing video:', err);
            });
        }
    }, [isVideo, fadeIn, currentIndex]);

    if (carouselItems.length === 0) {
        return null;
    }

    const hasUrl = currentItem.url && currentItem.url.trim() !== '';

    const handleClick = () => {
        if (hasUrl) {
            openExternalUrl(currentItem.url);
        }
    };

    return (
        <Box 
            sx={{ 
                width: '100%', 
                height: '100%', 
                position: 'relative',
                overflow: 'hidden',
                cursor: hasUrl ? 'pointer' : 'default',
                borderRadius: 1
            }}
            onClick={handleClick}
        >
            <Fade in={fadeIn} timeout={carouselSettings.transitionDuration}>
                <Box sx={{ width: '100%', height: '100%' }}>
                    {isVideo ? (
                        <video
                            ref={videoRef}
                            src={currentItem.image}
                            muted
                            playsInline
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderRadius: '4px'
                            }}
                        />
                    ) : (
                        <CardMedia
                            component="img"
                            image={currentItem.image}
                            alt={currentItem.alt || `Ad ${currentIndex + 1}`}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderRadius: 1
                            }}
                        />
                    )}
                </Box>
            </Fade>
        </Box>
    );
};

export default ImageCarousel;
